import type { StorefrontPageProps } from '@kitup/brand';
import { useEffect, useId, useRef, useState, type JSX } from 'react';

/** The account pages a signed-in visitor goes to, the same ones the app's own menu lists. */
const PLACES = [
  { to: '/app', label: 'My books' },
  { to: '/app/characters', label: 'Characters' },
  { to: '/app/things', label: 'Favorite things' },
  { to: '/app/locations', label: 'Locations' },
  { to: '/app/settings', label: 'Settings' },
] as const;

/**
 * The signed-in visitor's menu: a round button with their initial, which opens
 * the account pages and "Sign out". It stands where "Create a book" stands for
 * a guest, and it is the same on phones and desktops.
 */
export function AccountMenu({
  user,
  Link,
  onSignOut,
}: {
  readonly user: { readonly name: string };
  readonly Link: StorefrontPageProps['Link'];
  readonly onSignOut: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const initial = user.name.trim().charAt(0).toUpperCase() || '?';

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={root} className="ta-account">
      <button
        ref={button}
        type="button"
        className="ta-account-button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Account menu: ${user.name}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">{initial}</span>
      </button>
      {open ? (
        <nav id={menuId} className="ta-account-menu" aria-label="Account">
          <p className="ta-account-name">{user.name}</p>
          <ul>
            {PLACES.map((place) => (
              <li key={place.to}>
                <Link to={place.to} className="ta-account-item">
                  {place.label}
                </Link>
              </li>
            ))}
            <li>
              <button
                type="button"
                className="ta-account-item"
                onClick={() => {
                  setOpen(false);
                  onSignOut();
                }}
              >
                Sign out
              </button>
            </li>
          </ul>
        </nav>
      ) : null}
    </div>
  );
}
